import type { FunctionComponent } from 'react';
import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Typography } from '@material-ui/core';

type LoaderPositionType =
  | { screenCenter: true; containerRef: React.RefObject<HTMLDivElement | null> }
  | { screenCenter?: false; containerRef?: null };

export type LoaderProps = {
  className?: string;
  top?: number;
  /**
   * A short explanatory text positioned below the loading icon, with a larger font size.
   */
  loadingText?: string;
  /**
   * A long explanatory text positioned below the loading icon, with a regular font size.
   */
  longLoadingText?: string;
  containerRef?: React.RefObject<HTMLDivElement | null>;
} & LoaderPositionType;

/**
 * Spinning Jerry Logo.
 */
const UnstyledLoader: FunctionComponent<LoaderProps> = ({
  className,
  loadingText,
  screenCenter,
  containerRef,
  longLoadingText,
}) => {
  const loadingRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!screenCenter || typeof document === 'undefined' || !containerRef?.current || !loadingRef.current) {
      return;
    }
    const container = containerRef.current;
    const loading = loadingRef.current;

    const update = () => {
      const containerRect = container.getBoundingClientRect();
      const distanceTop = containerRect.top + window.scrollY;
      const topY = Math.max(window.scrollY, distanceTop);
      const bottomY = Math.min(containerRect.bottom + window.scrollY, window.innerHeight + window.scrollY);
      const center = (topY + bottomY) / 2 - distanceTop;

      const loadingRect = loading.getBoundingClientRect();
      const top = center - loadingRect.height / 2;
      loading.style.top = `${top}px`;
    };

    let rafID: number | undefined;
    const handler = () => {
      if (rafID) {
        cancelAnimationFrame(rafID);
      }
      rafID = requestAnimationFrame(update);
    };

    handler();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler);
    };
  }, [containerRef, screenCenter]);

  return (
    <div className={className} ref={loadingRef}>
      <div className="preloader">
        <div className="circle" />
        <div className="arch" />
      </div>
      {!!loadingText && (
        <div className="describe">
          <Typography variant="h6">{loadingText}</Typography>
        </div>
      )}
      {!!longLoadingText && (
        <Typography align="center" className="describe-long">
          {longLoadingText}
        </Typography>
      )}
    </div>
  );
};

const preloaderAnimation = keyframes`
  0% { transform: translateX(-50%) translateY(-50%) rotateZ(0deg); }
  100% { transform: translateX(-50%) translateY(-50%) rotateZ(360deg); }
`;

export const Loader = styled(UnstyledLoader)`
  position: absolute;
  top: ${({ top = 0 }) => `${top}px`};
  ${({ screenCenter }) => (screenCenter ? '' : 'width: 100%; height: 100%;')}
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .describe {
    position: absolute;
    transform: scale(2);
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    margin-top: 400px;
    transform: scale(2.5);
  }

  .describe-long {
    font-size: 5.8vw;
    padding: 0 20px;
    line-height: 6.8vw;
    font-weight: bold;
    color: #4b5466;
  }

  & .preloader {
    position: relative;
    width: 198px;
    height: 30px;
    padding-bottom: 168px;
    transform: scale(0.4);
  }
  & .arch {
    position: absolute;
    width: 198px;
    height: 198px;
    left: 50%;
    transform-origin: 50% 100%;
    transform: translateX(-50%) translateY(-50%) rotateZ(0deg);
    overflow: hidden;
    animation-name: ${preloaderAnimation};
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-timing-function: cubic-bezier(0.1, 0.25, 0.1, 1);
  }
  & .arch:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    left: 50%;
    top: 50%;
    transform: translateX(-50%);
    border: 30px solid ${({ theme }) => theme.palette.primary.main};
    border-radius: 50%;
    box-sizing: border-box;
  }
  & .circle {
    position: absolute;
    width: 40px;
    height: 40px;
    border-radius: 50px;
    background-color: ${({ theme }) => theme.palette.primary.main};
    top: 80px;
    left: 80px;
  }
`;
